import {
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Zap,
  Calendar,
  Clock,
  Home,
  Building,
  Users,
  CheckCircle2,
  Heart,
  Wrench,
  Briefcase,
  GraduationCap,
  Bed,
} from 'lucide-react'
import { useVerticalUI } from '@/lib/verticalUI/useVerticalUI'
import type { DashboardKpiIcon, VerticalKpiCardConfig } from '@/lib/verticalUI/types'
import type { DashboardStats } from '@/services/dashboardApi'

interface VerticalPerformanceSectionProps {
  stats: DashboardStats | null
  loading?: boolean
}

function renderKpiIcon(icon: DashboardKpiIcon) {
  switch (icon) {
    case 'shopping-cart':
      return <ShoppingCart className="w-5 h-5" />
    case 'dollar':
      return <DollarSign className="w-5 h-5" />
    case 'zap':
      return <Zap className="w-5 h-5" />
    case 'calendar':
      return <Calendar className="w-5 h-5" />
    case 'clock':
      return <Clock className="w-5 h-5" />
    case 'home':
      return <Home className="w-5 h-5" />
    case 'building':
      return <Building className="w-5 h-5" />
    case 'users':
      return <Users className="w-5 h-5" />
    case 'check-circle':
      return <CheckCircle2 className="w-5 h-5" />
    case 'heart':
      return <Heart className="w-5 h-5" />
    case 'wrench':
      return <Wrench className="w-5 h-5" />
    case 'briefcase':
      return <Briefcase className="w-5 h-5" />
    case 'graduation-cap':
      return <GraduationCap className="w-5 h-5" />
    case 'bed':
      return <Bed className="w-5 h-5" />
    default:
      return <TrendingUp className="w-5 h-5" />
  }
}

const colorStyles: Record<
  VerticalKpiCardConfig['colorScheme'],
  { card: string; iconBox: string; badge: string; valueText: string }
> = {
  emerald: {
    card: 'bg-gradient-to-br from-white to-emerald-50/40 border-emerald-100',
    iconBox: 'bg-emerald-500/10 text-emerald-600',
    badge: 'text-emerald-700 bg-emerald-100/70',
    valueText: 'text-slate-900',
  },
  blue: {
    card: 'bg-gradient-to-br from-white to-blue-50/40 border-blue-100',
    iconBox: 'bg-blue-500/10 text-blue-600',
    badge: 'text-blue-700 bg-blue-100/70',
    valueText: 'text-slate-900',
  },
  purple: {
    card: 'bg-gradient-to-br from-white to-purple-50/40 border-purple-100',
    iconBox: 'bg-purple-500/10 text-purple-600',
    badge: 'text-purple-700 bg-purple-100/70',
    valueText: 'text-purple-700',
  },
  amber: {
    card: 'bg-gradient-to-br from-white to-amber-50/40 border-amber-100',
    iconBox: 'bg-amber-500/10 text-amber-600',
    badge: 'text-amber-800 bg-amber-100/80',
    valueText: 'text-slate-900',
  },
  teal: {
    card: 'bg-gradient-to-br from-white to-teal-50/40 border-teal-100',
    iconBox: 'bg-teal-500/10 text-teal-600',
    badge: 'text-teal-700 bg-teal-100/70',
    valueText: 'text-slate-900',
  },
  rose: {
    card: 'bg-gradient-to-br from-white to-rose-50/40 border-rose-100',
    iconBox: 'bg-rose-500/10 text-rose-600',
    badge: 'text-rose-700 bg-rose-100/70',
    valueText: 'text-slate-900',
  },
  indigo: {
    card: 'bg-gradient-to-br from-white to-indigo-50/40 border-indigo-100',
    iconBox: 'bg-indigo-500/10 text-indigo-600',
    badge: 'text-indigo-700 bg-indigo-100/70',
    valueText: 'text-slate-900',
  },
}

function formatValue(raw: number | undefined | null, format?: 'number' | 'currency' | 'percent') {
  const val = raw ?? 0
  if (format === 'currency') {
    return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  if (format === 'percent') {
    return `${val}%`
  }
  return val.toLocaleString()
}

export function VerticalPerformanceSection({ stats, loading }: VerticalPerformanceSectionProps) {
  const { profile } = useVerticalUI()
  const dashboardConfig = profile.dashboard

  return (
    <section className="space-y-3" data-testid="vertical-performance-section">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
          {dashboardConfig.sectionTitle}
        </h3>
        {dashboardConfig.sectionBadge && (
          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
            {dashboardConfig.sectionBadge}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {dashboardConfig.kpis.map((kpi) => {
          const style = colorStyles[kpi.colorScheme] || colorStyles.emerald
          const rawValue = stats?.overview?.[kpi.dataKey]

          return (
            <div
              key={kpi.id}
              data-testid={`kpi-card-${kpi.id}`}
              className={`p-4 rounded-xl border shadow-xs ${style.card}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg ${style.iconBox}`}>
                  {renderKpiIcon(kpi.icon)}
                </div>
                {kpi.badgeLabel && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${style.badge}`}>
                    {kpi.badgeLabel}
                  </span>
                )}
              </div>
              <p className="text-xs font-semibold text-slate-500">{kpi.title}</p>
              <div className="flex items-baseline gap-1 mt-1">
                {loading && !stats ? (
                  <div className="skeleton h-8 w-20 rounded" data-testid="kpi-skeleton" />
                ) : (
                  <>
                    <p className={`text-2xl font-bold tracking-tight ${style.valueText}`}>
                      {formatValue(rawValue, kpi.format)}
                    </p>
                    {kpi.format !== 'currency' && kpi.format !== 'percent' && (
                      <p className="text-[10px] font-medium text-slate-400">{kpi.unit}</p>
                    )}
                  </>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">{kpi.description}</p>
            </div>
          )
        })}
      </div>
    </section>
  )
}
